import type { QueryClient } from '@tanstack/react-query';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, resolveCharIdFromJwt } from '@/lib/axios';

export interface CuponRow {
  id: number;
  cupon_code: string;
  name: string;
  description: string | null;
  start_date: string;
  end_date: string;
  isactive: number;
  level_required: number;
  limit_global: number;
  limit_per_jid: number;
  limit_per_charid: number;
  limit_per_hwid: number;
  total_claimed: number;
}

export interface CuponReward {
  id: number;
  cupon_id: number;
  CodeName128: string;
  SN_Name: string;
  image_url: string | null;
  Quantity: number;
  is_default: number;
  item_order: number;
}

export interface CuponsState {
  status: 'success';
  cupon: CuponRow | null;
  rewards: CuponReward[];
  email: string;
  whatsapp?: string;
  deliveryChannel?: 'email' | 'whatsapp';
  hasClaimed: boolean;
  hasActiveCode: boolean;
  codeExpires: string | null;
  message?: string;
}

export interface CuponsMutationResult {
  success: boolean;
  message?: string;
  codeExpires?: string;
}

function cuponsQueryKey(charId: number): (string | number)[] {
  return ['cupons', charId];
}

async function fetchCupons(): Promise<CuponsState> {
  const { data } = await api.get<CuponsState>('/api/v1/cupons');
  return data;
}

export function useCupons() {
  const charId = resolveCharIdFromJwt();
  return useQuery({
    queryKey: cuponsQueryKey(charId),
    queryFn: fetchCupons,
    enabled: charId > 0,
    staleTime: 30_000,
  });
}

export function useRequestCuponCode() {
  const queryClient = useQueryClient();
  const charId = resolveCharIdFromJwt();

  return useMutation({
    mutationFn: async (channel: 'email' | 'whatsapp') => {
      const { data } = await api.post<CuponsMutationResult>('/api/v1/cupons/request-code', {channel});
      return data;
    },
    onSuccess: (data) => {
      if (data.success) {
        void queryClient.invalidateQueries({ queryKey: cuponsQueryKey(charId) });
      }
    },
  });
}

export function useClaimCupon() {
  const queryClient = useQueryClient();
  const charId = resolveCharIdFromJwt();

  return useMutation({
    mutationFn: async (code: string) => {
      const { data } = await api.post<CuponsMutationResult>('/api/v1/cupons/claim', { code });
      return data;
    },
    onSuccess: (data) => {
      if (data.success) {
        window.setTimeout(() => {
          void queryClient.invalidateQueries({ queryKey: cuponsQueryKey(charId) });
        }, 2000);
      }
    },
  });
}

export function prefetchCupons(queryClient: QueryClient, charId: number): void {
  if (!charId) return;
  void queryClient.prefetchQuery({
    queryKey: cuponsQueryKey(charId),
    queryFn: fetchCupons,
    staleTime: 30_000,
  });
}
